import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isSurveyPath } from '../lib/surveyFunnel';

const AuthMagicLink: React.FC = () => {
  const { login, loginWithOtp, isLoading, isEmailSent, clearEmailSent, authError } = useAuth();
  const [email, setEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem('rhwb-pulse_user_email');
    const savedRememberMe = localStorage.getItem('rhwb-pulse_remember_me');
    if (savedEmail && savedRememberMe === 'true') {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleLogin = async () => {
    setLoginError('');
    if (!email) {
      setLoginError('Please enter your email address');
      return;
    }

    if (rememberMe) {
      localStorage.setItem('rhwb-pulse_user_email', email);
      localStorage.setItem('rhwb-pulse_remember_me', 'true');
    } else {
      localStorage.removeItem('rhwb-pulse_user_email');
      localStorage.removeItem('rhwb-pulse_remember_me');
    }

    const fromSurvey = isSurveyPath(window.location.pathname);
    const result = await login(email, rememberMe, fromSurvey ? { source: 'survey' } : {});
    if (!result.success) {
      setLoginError(result.error || 'Failed to send sign-in link');
    }
  };

  if (isLoading && !isEmailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const handleOtpSubmit = async () => {
    if (!/^\d{6}$/.test(otpCode)) {
      setOtpError('Please enter the 6-digit code from your email.');
      return;
    }
    setOtpLoading(true);
    setOtpError('');
    const result = await loginWithOtp(email, otpCode);
    setOtpLoading(false);
    if (!result.success) {
      setOtpError(result.error || 'Invalid or expired code. Please try again.');
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full text-center">
          <div className="bg-green-100 rounded-full p-4 mb-6 inline-block">
            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Check Your Email</h2>
          <p className="text-gray-600 mb-2">
            We sent a sign-in link to <strong>{email}</strong>.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Click the link in the email to sign in. If you don't see it, check your spam folder.
          </p>

          <div className="border-t border-gray-200 pt-6 mt-2 text-left">
            <p className="text-sm font-medium text-gray-700 mb-1 text-center">
              Signing in on a different device or browser?
            </p>
            <p className="text-xs text-gray-500 mb-4 text-center">
              Enter the 6-digit code from your email instead.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, '')); setOtpError(''); }}
                onKeyPress={(e) => { if (e.key === 'Enter') handleOtpSubmit(); }}
                className="flex-1 px-3 py-3 border border-gray-300 rounded-lg text-center text-xl font-mono tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="000000"
              />
              <button
                onClick={handleOtpSubmit}
                disabled={otpLoading || otpCode.length !== 6}
                className="px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {otpLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                ) : 'Verify'}
              </button>
            </div>
            {otpError && <p className="mt-2 text-sm text-red-600 text-center">{otpError}</p>}
          </div>

          <button
            onClick={clearEmailSent}
            className="text-blue-600 text-sm hover:text-blue-700 underline mt-6 block mx-auto"
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-xl mb-6 inline-block">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">RHWB Pulse</h1>
          <p className="text-gray-600">Sign in with your authorized email to access the dashboard</p>
        </div>

        {(loginError || authError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{loginError || authError}</p>
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center justify-center mb-3">
            <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Secure Authentication</span>
          </div>
          <p className="text-xs text-gray-500 text-center">
            We'll send you a secure sign-in link via email. No password needed.
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (loginError) setLoginError(''); }}
                onKeyPress={(e) => { if (e.key === 'Enter') handleLogin(); }}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter your authorized email address"
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="remember-me"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
              Remember me on this device
            </label>
          </div>

          <div className="text-xs text-gray-500">
            {rememberMe
              ? 'Your login is saved on this computer permanently until you sign out.'
              : 'Your login will only last for this browser session.'}
          </div>

          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Sending link...
              </div>
            ) : (
              'Sign In'
            )}
          </button>
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            Only authorized users can access this dashboard.
            If you need access, please{' '}
            <a
              href="mailto:tech@rhwb.org?subject=RHWB Pulse Access Request"
              className="text-blue-600 hover:text-blue-700 underline"
            >
              contact the RHWB Tech Team
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthMagicLink;
